import { memo, useEffect, useRef } from "react";

import classnames from "classnames";
import { noop, findById, isNotEmpty, hyphenate, isPresent } from "neetocist";
import { Drag } from "neetoicons";
import { Typography } from "neetoui";
import { mergeRight } from "ramda";

import MoreDropdown from "components/MoreDropdown";

const SelectedElement = ({
  element,
  isActive,
  isSelectedElementActionsDisabled,
  selectedElementActionsTooltipProps,
  handleSelectElement,
  isSelectable = true,
  errors = [],
  actions = [],
  isDraggable = false,
  ...props
}) => {
  const Icon = element.icon;
  const rootElementRef = useRef();

  const onClick = () => handleSelectElement(element);

  const shouldShowDropdown = isNotEmpty(actions);

  const menuItems =
    typeof actions === "function"
      ? actions(element)
      : actions.map(action =>
          mergeRight(action, {
            onClick: () => {
              action.onClick(element);
            },
          })
        );

  useEffect(() => {
    const rootElement = rootElementRef.current;

    if (
      !rootElement ||
      !isActive ||
      typeof rootElement?.scrollIntoView !== "function"
    ) {
      return;
    }
    rootElement.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [isActive, rootElementRef]);

  const error = findById(element.id, errors);

  return (
    <div
      data-testid={props["data-testid"]}
      ref={rootElementRef}
      className={classnames(
        "neeto-ui-border-gray-400 neeto-ui-bg-white hover:neeto-ui-bg-gray-200 neeto-ui-rounded-lg relative mb-2 flex h-10 cursor-pointer items-center gap-2 border py-3 ps-4 pe-3 transition-all duration-300 ease-in-out",
        { "neeto-molecules-builder-element--active": isActive },
        { "hover:neeto-ui-bg-error-100 neeto-ui-bg-error-100": error }
      )}
      onClick={isSelectable ? onClick : noop}
    >
      {isDraggable && (
        <span
          className="absolute end-full top-1/2 me-0.5 flex-shrink-0 -translate-y-1/2 cursor-grab"
          data-testid="drag-handle"
        >
          <Drag
            className="neeto-ui-text-gray-400 hover:neeto-ui-text-gray-500 transition-all duration-300 ease-in-out"
            size={16}
          />
        </span>
      )}
      <div
        className="flex flex-grow items-center gap-2 overflow-hidden"
        data-testid="new-element-field"
      >
        {Icon && (
          <div className="flex-shrink-0">
            <Icon />
          </div>
        )}
        <Typography
          className="truncate leading-snug"
          data-testid={`${hyphenate(element.label, "new-element")}-label`}
          style="body2"
        >
          {element.label}
        </Typography>
      </div>
      {shouldShowDropdown && (
        <MoreDropdown
          {...{ menuItems }}
          isDisabled={element.isPending || isSelectedElementActionsDisabled}
          dropdownButtonProps={{
            onClick: e => e.stopPropagation(),
            className: "min-h-0 flex-shrink-0",
            "data-testid": `${hyphenate(element.label)}-more-dropdown`,
            loading: element.isPending,
            ...(isPresent(selectedElementActionsTooltipProps) && {
              tooltipProps: selectedElementActionsTooltipProps,
            }),
          }}
        />
      )}
    </div>
  );
};

export default memo(SelectedElement);
